import React from "react";
import { Badge, Box, Button, Skeleton, SimpleGrid, Stack, Text } from "@chakra-ui/react";

const uniqueCities = (cities) => {
  const seen = new Set();
  return (cities || []).filter((city) => {
    const key = `${city?.empresa_id || "x"}:${String(city?.cidade || "").trim().toLowerCase()}`;
    if (!city?.cidade || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const normalizeCity = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim()
    .toLowerCase();

const resolveCityState = (city) => {
  const explicitState = String(city?.estado || city?.uf || "").trim().toUpperCase();
  if (explicitState) return explicitState;
  const normalizedCity = normalizeCity(city?.cidade);
  if (normalizedCity === "caldas" || normalizedCity === "cabo verde") {
    return "MG";
  }
  return "";
};

const CitySelector = ({ cities, assetUrl, onSelectCity, loading = false }) => {
  const items = uniqueCities(cities);

  return (
    <Box px={{ base: 4, md: 6 }} py={{ base: 4, md: 6 }}>
      <Stack spacing={3} maxW="980px" mx="auto" mb={6}>
        <Badge
          alignSelf="flex-start"
          px={3}
          py={1}
          borderRadius="full"
          bg="rgba(223, 255, 229, 0.18)"
          color="#e8ffe6"
          border="1px solid rgba(228, 255, 233, 0.32)"
        >
          Cidades do monitoramento
        </Badge>
        <Text color="#f4fbe9" fontSize={{ base: "2xl", md: "4xl" }} fontWeight="900" lineHeight="1">
          Selecione uma cidade
        </Text>
        <Text color="rgba(234, 245, 226, 0.88)" fontSize={{ base: "sm", md: "lg" }} maxW="720px">
          Escolha a cidade para abrir a rede de clientes, câmeras e monitoramento ativo.
        </Text>
      </Stack>

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 4, md: 6 }} maxW="980px" mx="auto">
        {loading
          ? Array.from({ length: 4 }).map((_, index) => (
              <Box
                key={`loading-${index}`}
                minH={{ base: "240px", md: "290px" }}
                borderRadius="28px"
                overflow="hidden"
                bg="rgba(16, 38, 27, 0.78)"
                border="1px solid rgba(235, 255, 241, 0.18)"
                boxShadow="0 24px 60px rgba(0, 0, 0, 0.26)"
                p={{ base: 4, md: 5 }}
              >
                <Stack h="100%" justify="space-between">
                  <Stack spacing={3}>
                    <Skeleton height="26px" width="140px" borderRadius="full" startColor="rgba(255,255,255,0.10)" endColor="rgba(255,255,255,0.22)" />
                    <Skeleton height={{ base: "34px", md: "46px" }} width="68%" borderRadius="16px" startColor="rgba(255,255,255,0.10)" endColor="rgba(255,255,255,0.22)" />
                    <Skeleton height="18px" width="84%" borderRadius="12px" startColor="rgba(255,255,255,0.10)" endColor="rgba(255,255,255,0.22)" />
                  </Stack>
                  <Skeleton height="40px" width="170px" borderRadius="full" startColor="rgba(255,255,255,0.10)" endColor="rgba(255,255,255,0.22)" />
                </Stack>
              </Box>
            ))
          : null}
        {items.map((city) => {
          const backgroundUrl = assetUrl(city.home_image_url) || assetUrl(city.background_image_url) || null;
          const stateLabel = resolveCityState(city);

          return (
            <Button
              key={`${city.empresa_id}-${city.id}`}
              onClick={() => onSelectCity?.(city.cidade)}
              h="auto"
              p={0}
              borderRadius="28px"
              overflow="hidden"
              textAlign="left"
              bg="rgba(16, 38, 27, 0.78)"
              border="1px solid rgba(235, 255, 241, 0.18)"
              boxShadow="0 24px 60px rgba(0, 0, 0, 0.26)"
              _hover={{ transform: "translateY(-2px)", bg: "rgba(19, 46, 32, 0.9)" }}
              _active={{ transform: "translateY(0)" }}
            >
              <Box
                w="100%"
                minH={{ base: "240px", md: "290px" }}
                position="relative"
                bgImage={
                  backgroundUrl
                    ? `linear-gradient(180deg, rgba(9, 23, 16, 0.14), rgba(7, 19, 13, 0.88)), url('${backgroundUrl}')`
                    : "linear-gradient(135deg, rgba(28,84,56,0.95), rgba(8,29,20,0.95))"
                }
                bgSize="cover"
                bgPosition="center"
              >
                <Box
                  position="absolute"
                  inset="0"
                  bg="radial-gradient(circle at top right, rgba(142, 255, 196, 0.18), transparent 28%), radial-gradient(circle at bottom left, rgba(255, 226, 142, 0.16), transparent 34%)"
                />
                <Stack position="relative" zIndex="1" h="100%" justify="space-between" align="stretch" p={{ base: 4, md: 5 }} spacing={5}>
                  <Stack spacing={2} align="flex-start">
                    <Badge
                      px={3}
                      py={1}
                      borderRadius="full"
                      bg="rgba(255,255,255,0.16)"
                      color="#f9fff5"
                      border="1px solid rgba(255,255,255,0.24)"
                    >
                      {city.visual_mode === "image" ? "Rede visual" : "Google Maps"}
                    </Badge>
                    <Text color="#f7fff4" fontSize={{ base: "2xl", md: "4xl" }} fontWeight="900" lineHeight="0.95">
                      {city.cidade}
                    </Text>
                    <Text color="rgba(239, 249, 235, 0.8)" fontSize={{ base: "sm", md: "md" }}>
                      {stateLabel ? `${stateLabel} • ` : ""}Clique para abrir os clientes desta cidade
                    </Text>
                  </Stack>

                  <Box
                    alignSelf="flex-start"
                    px={4}
                    py={2}
                    borderRadius="full"
                    bg="rgba(244, 255, 245, 0.12)"
                    border="1px solid rgba(244,255,245,0.2)"
                  >
                    <Text color="#eefcea" fontWeight="800" fontSize="sm">
                      Entrar na cidade
                    </Text>
                  </Box>
                </Stack>
              </Box>
            </Button>
          );
        })}
      </SimpleGrid>
    </Box>
  );
};

export default CitySelector;
